import React, { Component } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'

import Style from '../IncidentFeed.module.css'
import ThreatStatusTiles from './ThreatStatusTiles'
import TrendPanel from '../TrendPanel'
import ThreatFeedFilter from './ThreatFeedFilter'
import ThreatFeedTable from './ThreatFeedTable'
import ThreatFeedGrid from './ThreatFeedGrid'
import ThreatFeedDetailModal from './ThreatFeedDetailModal'
import ConfirmPopup from '../../common/popover/confirmPopup'
import { getBrands } from '../../../store/entities/customers'
import { loadEvents, setViewType, resetEventsModal, setThreatFiltersInStore } from '../../../store/entities/events'
import { isGlobalView } from '../../../utilities/config'
import { getUTCDate } from '../../../utilities/datetime'
import { eventsInitialState } from '../../../store/initialState/eventsState'

// ===========================================================================
// Incident Feed → Threat Feed tab (PIXMC-6500).
//
// Tiles + trend come from the incidentFeed slice — the SAME payloads the Overview tab's
// ring (overview/phishing-detections) and daily-activity panel (phishing-detections) use,
// fetched by IncidentFeed.jsx when this tab is active.
//
// The detections filter, table/grid, Update Status dropdown and View Details modal are all
// NEW components built to match the claude-dashboards mockup (ThreatFeed* in this folder);
// the legacy incidents.* / UpdatePopover UI is left untouched. Only the UI is isolated — the
// Redux state (events slice) is shared, so these dispatch the same events thunks and get
// pagination / filtering / status-change wiring for free. Chart view is deferred this
// release (client decision), so only list/grid render here.
// ===========================================================================

// Fresh default filters for a customer/tab entry — 30-day window, no brand/user/status.
// Reset into the shared slice on every (re)load so the query can never run with the previous
// customer's (or the legacy Incidents screen's) leftover filters while the bar shows "All".
const defaultThreatFilters = () => ({
  ...eventsInitialState.threat.filters,
  startTime: getUTCDate(30),
  endTime: getUTCDate(0),
  offset: 0,
})

class ThreatFeed extends Component {
  state = { viewType: this.props.viewType === 'grid' ? 'grid' : 'list', detailEvent: null, pendingActionLabel: null }

  componentDidMount() {
    // Load whenever the tab mounts and a customer is selected — NOT gated on the URL :id.
    // ThreatFeed is nested inside IncidentFeed, so its router match does not reliably carry
    // :id; gating on it meant returning to the tab (customer already selected, no change
    // event) skipped the events call and left an empty table. loadEvents keys off
    // selectedCustomer.id, so loading on mount is always for the right customer.
    if (this.props.selectedCustomer) this.loadForCustomer(this.props.selectedCustomer)
  }

  componentDidUpdate(prevProps) {
    if (prevProps.selectedCustomer !== this.props.selectedCustomer && this.props.selectedCustomer) {
      this.loadForCustomer(this.props.selectedCustomer)
    }
  }

  loadForCustomer = (selectedCustomer) => {
    const global = isGlobalView(selectedCustomer.organizationName)
    // Start every entry / customer-switch from clean defaults so the shared
    // events.threat.filters can't carry another customer's brand/user/status while the
    // filter bar reads "All".
    const filters = defaultThreatFilters()
    this.props.setThreatFiltersInStore(filters)
    this.props.getBrands(selectedCustomer.id, selectedCustomer?.defaultCustomerId, global)
    this.props.loadEvents(filters, selectedCustomer.id, selectedCustomer.defaultCustomerId, global)
  }

  handleViewSelect = (viewType) => {
    // Chart view is deferred this release (PIXMC-6500); only list/grid are offered.
    if (viewType === 'chart') return
    this.setState({ viewType })
    this.props.setViewType(viewType)
  }

  // View Details opens a mockup-styled popup over the clicked row (Option A). The all-events
  // row already carries the screenshot / page-content URLs, so no extra fetch is needed.
  handleDetails = (record) => this.setState({ detailEvent: record })

  closeDetail = () => this.setState({ detailEvent: null })

  // Called synchronously when a row/popup action is confirmed — BEFORE the acting row (and
  // its status menu) unmounts on the optimistic list update — so the central success popup
  // can show an action-specific message. Resetting first clears any stale status code so the
  // popup only appears once THIS action's thunk flips it. (Unverify owns its own result popup
  // in ThreatFeedStatusMenu, via threatintel.verifyStatusCode, so it does not route here.)
  handleStatusAction = (label) => {
    this.props.resetEventsModal()
    this.setState({ pendingActionLabel: label })
  }

  // Clear / Mark False Positive set events.statusCode; the result is surfaced through the
  // same ConfirmPopup used for the confirm/warning dialogs. Closing it dismisses the detail
  // popup (the event was just actioned) and clears the shared status code + pending label.
  handleStatusClose = () => {
    this.props.resetEventsModal()
    this.setState({ detailEvent: null, pendingActionLabel: null })
  }

  render() {
    const view = this.state.viewType === 'grid' ? 'grid' : 'list'
    const {
      windowLabel,
      overview, overviewLoading, overviewError,
      phishingDetections, phishingDetectionsLoading, phishingDetectionsError,
      eventStatusCode,
    } = this.props
    const trendTotal = Number(phishingDetections?.totalRecords || 0).toLocaleString()

    return (
      <>
        <div className={Style.sectionTitle}>
          Threat Feed
          <span className={Style.sectionWindow}>{windowLabel}</span>
        </div>

        <ThreatStatusTiles
          overview={overview}
          loading={overviewLoading}
          error={overviewError}
        />

        <div className={Style.card}>
          <div className={Style.cardHeader}>
            <div className={Style.cardTitle}>
              Daily phishing detections
              <span className={Style.cardSub}>{windowLabel}</span>
            </div>
          </div>
          <div className={Style.trendGrid} style={{ gridTemplateColumns: '1fr' }}>
            <TrendPanel
              name="Phishing detections"
              subtitle={`${trendTotal} total · ${windowLabel.toLowerCase()}`}
              records={phishingDetections?.records}
              loading={phishingDetectionsLoading}
              error={phishingDetectionsError}
              emptyText="No phishing detections"
              formatValue={(v) =>
                `${Number(v || 0).toLocaleString()} ${Number(v) === 1 ? 'detection' : 'detections'}`
              }
            />
          </div>
        </div>

        <div className={Style.card}>
          <ThreatFeedFilter viewType={view} onViewSelect={this.handleViewSelect} />
          {view === 'grid' ? (
            <ThreatFeedGrid onDetails={this.handleDetails} onStatusAction={this.handleStatusAction} />
          ) : (
            <ThreatFeedTable onDetails={this.handleDetails} onStatusAction={this.handleStatusAction} />
          )}
        </div>

        {this.state.detailEvent && (
          <ThreatFeedDetailModal
            event={this.state.detailEvent}
            onClose={this.closeDetail}
            onStatusAction={this.handleStatusAction}
          />
        )}

        {this.state.pendingActionLabel && eventStatusCode ? (
          <ConfirmPopup
            details={{
              icon: '',
              type: eventStatusCode === 200 ? 'success' : 'error',
              title: eventStatusCode === 200 ? 'Success' : 'Action failed',
              description:
                eventStatusCode === 200
                  ? this.state.pendingActionLabel
                  : 'Something went wrong. Please try again.',
              buttons: 'O',
            }}
            onCloseClick={this.handleStatusClose}
          />
        ) : null}
      </>
    )
  }
}

const mapStateToProps = (state) => ({
  selectedCustomer: state.entities.customers.selected,
  viewType: state.entities.events.threatView.currentView,
  eventStatusCode: state.entities.events.statusCode,
  overview: state.entities.incidentFeed.overview,
  overviewLoading: state.entities.incidentFeed.overviewLoading,
  overviewError: state.entities.incidentFeed.overviewError,
  phishingDetections: state.entities.incidentFeed.phishingDetections,
  phishingDetectionsLoading: state.entities.incidentFeed.phishingDetectionsLoading,
  phishingDetectionsError: state.entities.incidentFeed.phishingDetectionsError,
})

const mapDispatchToProps = (dispatch) => ({
  loadEvents: (filters, selectedId, defaultCustomerId, isGlobalView) =>
    dispatch(loadEvents(filters, selectedId, defaultCustomerId, isGlobalView)),
  getBrands: (selectedId, defaultCustomerId, isGlobalView) =>
    dispatch(getBrands(selectedId, defaultCustomerId, isGlobalView)),
  setThreatFiltersInStore: (filters) => dispatch(setThreatFiltersInStore(filters)),
  setViewType: (viewType) => dispatch(setViewType(viewType)),
  resetEventsModal: () => dispatch(resetEventsModal()),
})

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ThreatFeed))
